/**
 * Partial destruction step 4 (ARCHITECTURE §5.13, 63.4's future, "parts that act"; decision 141): per-part health on the
 * marked parts of a structure (constants/damage.ts STRUCTURE_PARTS), with GameConfig.partialDestruction on.
 *
 * - A marked part keeps its own health in `StructureState.parts` (in STRUCTURE_PARTS' order; absent: every part whole —
 *   so with the switch off nothing here ever reads anything but "whole").
 * - A blast reaches each marked part by its own distance (core/blastReach.ts gives them under the shape policy) and the
 *   part takes its share of what the body would take from there, out of its own health (its share of the body's): it
 *   wears as the body does, faster from a blast nearer it. The body keeps its one number.
 * - A part at 0 is gone: its shape leaves the body's (core/hits.ts), so a lost shield drops the cover it gave, and its role
 *   stops — the gun is silent, the tower blind. On the way there a gun wears (`gunWear`): wider and slower (141).
 * Pure.
 */
import type { GameState, StructureState } from '../types';
import { STRUCTURE_PARTS, type MarkedPart } from '../constants/damage';
import { BODIES } from '../constants/body';

const NONE: readonly MarkedPart[] = [];

/** A structure type's marked parts (none for most). */
export function markedParts(type: StructureState['type']): readonly MarkedPart[] {
  return STRUCTURE_PARTS[type] ?? NONE;
}

/** A marked part's whole health: its share of the body's. */
export function partHealthMax(type: StructureState['type'], k: number): number {
  return markedParts(type)[k].share * BODIES[type].health;
}

/** The `k`-th marked part's health now (whole when the structure carries no part healths). */
export function partHealth(s: Pick<StructureState, 'type' | 'parts'>, k: number): number {
  return s.parts?.[k] ?? partHealthMax(s.type, k);
}

/** Is hit-shape part `index` of this structure gone (a marked part at 0)? */
export function hitPartGone(s: Pick<StructureState, 'type' | 'parts'>, index: number): boolean {
  if (!s.parts) return false;
  const k = markedParts(s.type).findIndex(m => m.part === index);
  return k >= 0 && s.parts[k] <= 0;
}

/** Has this structure lost a part with this role? */
export function roleLost(s: Pick<StructureState, 'type' | 'parts'>, role: MarkedPart['role']): boolean {
  if (!s.parts) return false;
  return markedParts(s.type).some((m, k) => m.role === role && s.parts![k] <= 0);
}

/**
 * How worn a gun is, 0 (whole) … 1 (silent), with partialDestruction on: the emplacement's by its gun part (the breech);
 * a mounted gun (the HMG, whose gun is not a target) by the body's health. 0 with the switch off.
 */
export function gunWear(state: Pick<GameState, 'config'>, s: Pick<StructureState, 'type' | 'parts' | 'health'>): number {
  if (!state.config.partialDestruction) return 0;
  const k = markedParts(s.type).findIndex(m => m.role === 'function');
  if (k >= 0) return Math.max(0, Math.min(1, 1 - partHealth(s, k) / partHealthMax(s.type, k)));
  return s.type === 'hmg' ? Math.max(0, Math.min(1, 1 - s.health / BODIES[s.type].health)) : 0;
}

/**
 * A blast on the marked parts: `dmgAt(dist)` is the damage the body would take at that distance (the body's falloff, its
 * side's share and the blow's share on the body's material); a part takes its share of it, from its own distance
 * (`dists[k]`, Infinity: unreached) — so it wears as the body does from a blast as far from both, faster from one nearer it.
 * Returns the new part healths, or null when no part was touched.
 */
export function blastParts(s: Pick<StructureState, 'type' | 'parts'>, dists: readonly number[], dmgAt: (dist: number) => number): number[] | null {
  const marked = markedParts(s.type);
  if (marked.length === 0) return null;
  let next: number[] | null = null;
  for (let k = 0; k < marked.length; k++) {
    const h = partHealth(s, k);
    if (h <= 0) continue;
    const d = dmgAt(dists[k] ?? Infinity) * marked[k].share;
    if (!(d > 0)) continue;
    (next ??= marked.map((_, j) => partHealth(s, j)))[k] = Math.max(0, h - d);
  }
  return next;
}
